import { useState } from "react";

import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

import {
  useMutation,
  useLazyQuery,
} from "@apollo/client";

import BasicCommandModal from "components/BasicCommandModal";
import ChangeConfigModal from "components/ChangeConfigModal";
import { RoleAccessButton } from "@evos/components";

import ampolAccountIds from "lib/constants/ampolAccountIds.json";
import { normalizeChargerConfigValue, orderClmAbsLimitBeforeMode } from "lib/functions/charger";

import { 
  CLEAR_CACHE,
  GET_CONFIGURATION,
  GET_CONFIGURATION_OCPP,
  CHANGE_CONFIGURATION,
  RESET,
  UPDATE_FIRMWARE,
  // GET_TEST_REPORT_PDF,
  NOTIFY_AMPOL_INSTALL,
  UNINSTALL_CHARGER
} from "../gql";

import { 
  ChangeConfigData,
  ChargersConnection,
  zodGetConfigFormArguments,
  zodResetFormArguments,
  zodUpdateFirmwareFormArguments,
} from "types";

const SETTABLE_CHARGER_CONFIG_KEYS = [
  "ocppId",
  "ocppURL",
  "maxCurrent",
  "phasesConnected",
  "nominalSupplyVoltage",
  "chgrMode",
  "CurrentLimitMode",
  "ClmAbsLimit",
  "siteMeterSource",
] as const;

type ChargerCommandsProps = {
  userRoles: string[];
  serial: string;
  servicedById?: string | null;
};

export default function ChargerCommands({
  userRoles,
  serial,
  servicedById,
}: ChargerCommandsProps) {

  // Charger command mutations
  const [clearCache, { 
    loading: clearCacheLoading, 
    data: clearCacheData, 
    error: clearCacheError, 
    reset: resetClearCacheMutation 
  }] = useMutation(CLEAR_CACHE, {
    variables: { serial: serial},
    notifyOnNetworkStatusChange: true
  });
  const [getConfig, { 
    loading: getConfigLoading, 
    error: getConfigError,
  }] = useLazyQuery<ChargersConnection>(GET_CONFIGURATION, {
    variables: { n: 1, where: { serial: { eq: serial } } },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      setGetConfigResult(data);
    }
  });
  const [getConfigurationOcpp, { 
    loading: getConfigurationOcppLoading, 
    error: getConfigurationOcppError,
  }] = useLazyQuery(GET_CONFIGURATION_OCPP, {
    variables: { serial },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "no-cache",
  });
  const [changeConfig, { 
    loading: changeConfigLoading, 
  }] = useMutation<ChangeConfigData>(CHANGE_CONFIGURATION, {
    variables: { serial: serial},
    notifyOnNetworkStatusChange: true
  });
  const [reset, { 
    loading: resetLoading, 
    data: resetData, 
    error: resetError,
    reset: resetResetMutation
  }] = useMutation(RESET, {
    variables: { payload: {type: "Soft"}, serial: serial},
    notifyOnNetworkStatusChange: true
  });
  const [updateFirmware, {
    data: updateFirmwareData,
    loading: updateFirmwareLoading,
    error: updateFirmwareError,
    reset: resetUpdateFirmwareMutation
  }] = useMutation(UPDATE_FIRMWARE, {
    variables: { serial: serial },
    notifyOnNetworkStatusChange: true
  });
  const [notifyAmpolInstall, {
    data: notifyAmpolInstallData,
    loading: notifyAmpolInstallLoading,
    error: notifyAmpolInstallError,
    reset: resetNotifyAmpolInstallMutation
  }] = useMutation(NOTIFY_AMPOL_INSTALL, {
    variables: { serial: serial},
    notifyOnNetworkStatusChange: true
  });
  const [uninstallCharger, {
    data: uninstallChargerData,
    loading: uninstallChargerLoading,
    error: uninstallChargerError,
    reset: resetUninstallCharger
  }] = useMutation(UNINSTALL_CHARGER, {
    variables: { serial: serial},
    notifyOnNetworkStatusChange: true
  });

  const [ showGetConfigModal, setShowGetConfigModal ] = useState(false);
  const [ showChangeConfigModal, setShowChangeConfigModal ] = useState(false);
  const [ showClearCacheModal, setShowClearCacheModal ] = useState(false);
  const [ showResetModal, setShowResetModal ] = useState(false);
  const [ showNotifyAmpolInstallModal, setShowNotifyAmpolInstallModal ] = useState(false);
  const [ showUninstallChargerModal, setShowUninstallChargerModal ] = useState(false);
  const [ showUpdateFirmwareModal, setShowUpdateFirmwareModal ] = useState(false);

  type CommandFeedback = { configItem: string; message?: string };

  const [ currentConfigState, setCurrentConfigState ] = useState<Record<string, string>>({});
  const [ configModalCommandErrors, setConfigModalCommandErrors ] = useState<CommandFeedback[]>([]);
  const [ configModalCommandPending, setConfigModalCommandPending ] = useState<CommandFeedback[]>([]);
  const [ configModalCommandSuccess, setConfigModalCommandSuccess ] = useState<CommandFeedback[]>([]);

  const [ getConfigResult, setGetConfigResult ] = useState<ChargersConnection | null>(null);

  const handleSetCurrentConfigState = async (serial?: string) => {
    if (serial != null) {
      await getConfig({
        variables: {
          n: 1,
          where: { serial: { eq: serial } },
        },
        onCompleted: (data) => {
          // Saves current config to state when modal is opened
          // Object shape matches the shape of the form data
          const chargerConfiguration = data?.chargers?.edges?.[0]?.node?.charger_configuration ?? [];
          const writableValues: Record<string, string> = Object.fromEntries(
            chargerConfiguration
              .filter((item) => item.readonly === false)
              .map((item) => [item.config_key, item.charger_value ?? ""])
          );
          const configKeyData = Object.fromEntries(
            SETTABLE_CHARGER_CONFIG_KEYS.map((key) => [
              key,
              normalizeChargerConfigValue(key, writableValues[key] ?? ""),
            ])
          );
          setCurrentConfigState(configKeyData);
        }
      });
    }
  };

  const handleChangeConfigModalShow = (show: boolean) => {
    if (!show) {
      setCurrentConfigState({});
      if (configModalCommandErrors.length > 0) {
        setConfigModalCommandErrors([]);
      }
      if (configModalCommandPending.length > 0) {
        setConfigModalCommandPending([]);
      }
      if (configModalCommandSuccess.length > 0) {
        setConfigModalCommandSuccess([]);
      }
    }
    setShowChangeConfigModal(show);
  };

  const addConfigError = (configItem: string, message: string) => {
    setConfigModalCommandPending((prevPending) => prevPending.filter((pending) => pending.configItem !== configItem));
    setConfigModalCommandSuccess((prevSuccess) => prevSuccess.filter((success) => success.configItem !== configItem));
    setConfigModalCommandErrors((prevErrors) => {
      const filtered = prevErrors.filter((error) => error.configItem !== configItem);
      return [...filtered, { configItem, message }];
    });
  };

  const configurationApplied = (status: string | undefined) => {
    return status === "Accepted" || status === "RebootRequired";
  };

  const onConfigModalFormSubmit = async (data: Record<string, string>) => {
    const changedValues = orderClmAbsLimitBeforeMode(
      Object.entries(data).filter(([key, value]) => value !== currentConfigState[key])
    );
    let capacityLimitBlockedMode = false;
    for (const [key, value] of changedValues) {
      if (key === "CurrentLimitMode" && capacityLimitBlockedMode) {
        addConfigError(key, "Not changed because ClmAbsLimit was not accepted");
        continue;
      }

      const trimmedValue = (value ?? "").toString().trim();
      const trimmedCurrentValue = (currentConfigState[key] ?? "").toString().trim();

      if (trimmedValue === trimmedCurrentValue) {
        continue;
      }

      if (trimmedValue.length === 0) {
        addConfigError(key, "Value is required");
        if (key === "ClmAbsLimit") {
          capacityLimitBlockedMode = true;
        }
        continue;
      }

      if (trimmedValue.length > 500) {
        addConfigError(key, "Value must be between 1 and 500 characters");
        if (key === "ClmAbsLimit") {
          capacityLimitBlockedMode = true;
        }
        continue;
      }

      try {
        const result = await changeConfig({
          variables: { 
            payload: { key: key, value: trimmedValue}, 
            serial: serial
          },
          onError: (e) => {
            if (e.message?.includes("Charger response not available")) {
              setConfigModalCommandPending((prevPending) => {
                const existingPending = prevPending.find((p) => p.configItem === key);
                if (existingPending != null) {
                  return prevPending.map((p) => p.configItem === key ? {configItem: key} : p);
                } else {
                  return [...prevPending, {configItem: key}];
                }
              });
            } else {
              setConfigModalCommandPending((prevPending) => prevPending.filter((pending) => pending.configItem !== key));
              setConfigModalCommandErrors((prevErrors) => {
                const existingError = prevErrors.find((e) => e.configItem === key);
                if (existingError != null) {
                  return prevErrors.map((e) => e.configItem === key ? { configItem: key, message: e.message ?? existingError.message } : e);
                } else {
                  return [...prevErrors, { configItem: key, message: e.message }];
                }
              });
            }
          },
          onCompleted: (completed) => {
            const status = completed?.charger_ocpp_mutation?.change_configuration?.payload?.status;
            if (!configurationApplied(status)) {
              addConfigError(key, status != null && status.length > 0
                ? `Charger responded ${status}`
                : "Charger rejected the change");
              return;
            }
            setConfigModalCommandErrors((prevErrors) => prevErrors.filter((error) => error.configItem !== key));
            setConfigModalCommandSuccess((prevSuccess) => {
              const existingSuccess = prevSuccess.find((s) => s.configItem === key);
              if (existingSuccess != null) {
                return prevSuccess.map((s) => s.configItem === key ? {configItem: key} : s);
              } else {
                return [...prevSuccess, {configItem: key}];
              }
            });
          },
        });
        const status = result.data?.charger_ocpp_mutation?.change_configuration?.payload?.status;
        if (key === "ClmAbsLimit" && !configurationApplied(status)) {
          capacityLimitBlockedMode = true;
        }
      }
      catch (e) {
        console.error(e);
        if (key === "ClmAbsLimit") {
          capacityLimitBlockedMode = true;
          continue;
        }
        return;
      }
    }
  };

  return (
    <div id="ChargerCommands">
      <Row className="d-flex justify-content-center pt-4">
        <Col xs={12} className="d-flex flex-wrap gap-2 mb-2">
          <RoleAccessButton 
            variant="outline-primary" 
            onClick={() => setShowGetConfigModal(true)}
            userRoles={userRoles}
            expectedRole="EvosChargerViewer"
            tooltipId="get-config"
          >
            Get Configuration
          </RoleAccessButton>
          <RoleAccessButton 
            variant="outline-primary" 
            onClick={() => setShowChangeConfigModal(true)}
            userRoles={userRoles}
            expectedRole="EvosChargerEditor"
            tooltipId="change-config"
          >
            Set Configuration
          </RoleAccessButton>
          <RoleAccessButton 
            variant="outline-primary" 
            onClick={() => setShowClearCacheModal(true)}
            userRoles={userRoles}
            expectedRole="EvosChargerEditor"
            tooltipId="clear-cache"
          >
            Clear Cache
          </RoleAccessButton>
          <RoleAccessButton 
            variant="outline-primary" 
            onClick={() => setShowResetModal(true)}
            userRoles={userRoles}
            expectedRole="EvosChargerEditor"
            tooltipId="reset-charger"
          >
            Reset
          </RoleAccessButton>
          <RoleAccessButton 
            expectedRole="EvosChargerEditor"
            userRoles={userRoles}
            variant="outline-primary" 
            onClick={() => setShowUpdateFirmwareModal(true)}
            tooltipId="update-firmware"
          >
            Update Firmware
          </RoleAccessButton>
          {/* <Button 
            variant="outline-primary" 
            onClick={() => getPdf()}
            isLoading={getPdfLoading}
          >
            Get Test Report
          </Button> */}
          { (ampolAccountIds[(process.env.REACT_APP_ENV as "staging" | "production")]) === servicedById
            && <RoleAccessButton 
              variant="outline-primary" 
              onClick={() => setShowNotifyAmpolInstallModal(true)}
              userRoles={userRoles}
              expectedRole="EvosChargerEditor"
              tooltipId="ampol-installation-event"
            >
              Send Ampol Installation Event
            </RoleAccessButton>
          }
          <RoleAccessButton 
            variant="outline-primary" 
            onClick={() => setShowUninstallChargerModal(true)}
            userRoles={userRoles}
            expectedRole="EvosChargerEditor"
            tooltipId="uninstall-charger"
          >
            Uninstall Charger
          </RoleAccessButton>
        </Col>
      </Row>
      <BasicCommandModal
        showModal={showGetConfigModal}
        setShowModal={setShowGetConfigModal}
        header="Get Configuration"
        commandResponse={getConfigResult}
        resetMutation={() => setGetConfigResult(null)}
        isResponseLoading={getConfigLoading || getConfigurationOcppLoading}
        responseError={getConfigError ?? getConfigurationOcppError}
        formSubmitFn={async () => {
          setGetConfigResult(null);
          const ocppResult = await getConfigurationOcpp({ variables: { serial } });
          if (ocppResult.error != null) return;
          await getConfig({ variables: { n: 1, where: { serial: { eq: serial } } } });
        }}
        zodArgumentResolver={zodGetConfigFormArguments}
      />
      <ChangeConfigModal
        showChangeConfigModal={showChangeConfigModal}
        setShowChangeConfigModal={handleChangeConfigModalShow}
        serialSelected={serial}
        currentConfigState={currentConfigState}
        getConfigStatus={getConfigLoading
          ? "loading"
          : getConfigError != null
            ? "error"
            : getConfigResult != null
              ? "success"
              : "idle"
        }
        submitErrors={configModalCommandErrors}
        submitSuccess={configModalCommandSuccess}
        submitPending={configModalCommandPending}
        submitLoading={changeConfigLoading}
        handleSetCurrentConfigState={handleSetCurrentConfigState}
        onConfigFormSubmit={onConfigModalFormSubmit}
      />
      <BasicCommandModal
        showModal={showUpdateFirmwareModal}
        setShowModal={setShowUpdateFirmwareModal}
        header="Update Firmware"
        commandResponse={updateFirmwareData}
        resetMutation={resetUpdateFirmwareMutation}
        isResponseLoading={updateFirmwareLoading}
        responseError={updateFirmwareError}
        formStructure={[
          { 
            inputName: "location", 
            inputType: "text",
            placeholder: "Enter firmware location URL"
          },
        ]}
        formSubmitFn={(data) => updateFirmware({
          variables: { 
            payload: { location: data.location, retrieveDate: new Date(Date.now()).toISOString()},
            serial: serial
          }
        })}
        zodArgumentResolver={zodUpdateFirmwareFormArguments}
      />
      <BasicCommandModal
        showModal={showClearCacheModal}
        setShowModal={setShowClearCacheModal}
        header="Clear Cache"
        commandResponse={clearCacheData}
        resetMutation={resetClearCacheMutation}
        isResponseLoading={clearCacheLoading}
        responseError={clearCacheError}
        formSubmitFn={() => clearCache({variables: { serial: serial}})}
      />
      <BasicCommandModal
        showModal={showResetModal}
        setShowModal={setShowResetModal}
        header="Reset"
        commandResponse={resetData}
        resetMutation={resetResetMutation}
        isResponseLoading={resetLoading}
        responseError={resetError}
        formStructure={[
          { 
            inputName: "type", 
            inputType: "select",
            selectOptions: ["Hard", "Soft"]
          }
        ]}
        formSubmitFn={(data) => reset({variables: { payload: { type: data.type}, serial: serial}})}
        zodArgumentResolver={zodResetFormArguments}
      />
      { (ampolAccountIds[(process.env.REACT_APP_ENV as "staging" | "production")]) === servicedById
        && <BasicCommandModal
          showModal={showNotifyAmpolInstallModal}
          setShowModal={setShowNotifyAmpolInstallModal}
          header="Send Ampol Installation Event"
          commandResponse={notifyAmpolInstallData}
          resetMutation={resetNotifyAmpolInstallMutation}
          isResponseLoading={notifyAmpolInstallLoading}
          responseError={notifyAmpolInstallError}
          formSubmitFn={() => notifyAmpolInstall({variables: { serial: serial}})}
        />
      }
      <BasicCommandModal
        showModal={showUninstallChargerModal}
        setShowModal={setShowUninstallChargerModal}
        header="Uninstall Charger"
        commandResponse={
          uninstallChargerData != null
            ? uninstallChargerData?.uninstall_charger?.edges?.[0]?.node?.salesforce_details?.installation_ref == null
              ? { status: "Uninstall successful"}
              : { status: "Uninstall failed"}
            : null
        }
        resetMutation={resetUninstallCharger}
        isResponseLoading={uninstallChargerLoading}
        responseError={uninstallChargerError}
        formSubmitFn={() => uninstallCharger({variables: { serial: serial}})}
        modalBodyText="This will remove the charger's installation record and set its owner as Production AU. Proceed?"
      />
    </div>
  );
}
